import type {
  AccountResponse,
  AccountTreeRow,
  CreateAccountRequest,
  UpdateAccountRequest,
} from '@palaccount/contracts';
import { DEFAULT_PAGE_SIZE } from '@palaccount/contracts';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { api } from '@/api';
import { throwApiError } from '@/lib/server-messages';

/**
 * FIN-01 data layer. The flat list feeds every account picker in the product
 * (the receipts/payments/bills editors derive theirs from it); the tree and
 * ledger queries are the Chart of Accounts screen's own — posted-only
 * balances (R9), rolled up (BR-FIN-01-07), period-filtered (BR-FIN-01-14).
 */

export const ACCOUNTS_KEY = ['accounts'] as const;

export function useAccounts() {
  return useQuery({
    queryKey: ACCOUNTS_KEY,
    queryFn: async (): Promise<AccountResponse[]> => {
      const response = await api.accounts.list();
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.accounts;
    },
    // The chart changes rarely; every account write invalidates ACCOUNTS_KEY.
    staleTime: 60_000,
  });
}

export interface Period {
  from?: string | undefined;
  to?: string | undefined;
}

export function useAccountsTree(period: Period = {}) {
  return useQuery({
    queryKey: [...ACCOUNTS_KEY, 'tree', period],
    queryFn: async (): Promise<{
      accounts: AccountTreeRow[];
      totalDebit: string;
      totalCredit: string;
    }> => {
      const response = await api.accounts.tree({
        query: {
          ...(period.from ? { from: period.from } : {}),
          ...(period.to ? { to: period.to } : {}),
        },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

/**
 * FIN-01 §5d's drill-down, one page at a time (NFR-42).
 *
 * The three footer figures come back beside the rows and are figures about the
 * whole PERIOD, and the `balance` column is a SQL window over the same period —
 * so turning a page continues the running balance instead of restarting it.
 */
export function useAccountLedger(
  accountId: string | undefined,
  period: Period = {},
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
) {
  return useQuery({
    queryKey: [...ACCOUNTS_KEY, 'ledger', accountId, period, page, pageSize],
    enabled: Boolean(accountId),
    queryFn: async () => {
      const response = await api.accounts.ledger({
        params: { accountId: accountId! },
        query: {
          ...(period.from ? { from: period.from } : {}),
          ...(period.to ? { to: period.to } : {}),
          page,
          pageSize,
        },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

export function useCreateAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateAccountRequest): Promise<AccountResponse> => {
      const response = await api.accounts.create({ body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ACCOUNTS_KEY }),
  });
}

export function useUpdateAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      accountId,
      body,
    }: {
      accountId: string;
      body: UpdateAccountRequest;
    }): Promise<AccountResponse> => {
      const response = await api.accounts.update({ params: { accountId }, body });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ACCOUNTS_KEY }),
  });
}

export function useDeleteAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (accountId: string): Promise<void> => {
      const response = await api.accounts.remove({ params: { accountId } });
      if (response.status !== 204) throwApiError(response.status, response.body);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ACCOUNTS_KEY }),
  });
}

/** BR-FIN-01-19 — the empty-state button. */
export function useLoadStarterChart() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<AccountResponse[]> => {
      const response = await api.accounts.loadStarterChart();
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body.accounts;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ACCOUNTS_KEY }),
  });
}

export interface AccountOption {
  id: string;
  code: string;
  name: string;
}

const toOption = ({ id, code, name }: AccountResponse): AccountOption => ({ id, code, name });

/**
 * BR-FIN-03-04 — "Received into" offers the accounts INSIDE Cash & Cash
 * Equivalents: the postable, active children of the 1110 header. The server
 * re-checks the same rule on every write.
 */
export function useCashAccountOptions(): { options: AccountOption[]; isPending: boolean } {
  const { data: accounts, isPending } = useAccounts();
  const options = useMemo(() => {
    const header = (accounts ?? []).find((account) => account.code === '1110');
    if (!header) return [];
    return (accounts ?? [])
      .filter(
        (account) => account.parentId === header.id && account.postable && account.active,
      )
      .map(toOption);
  }, [accounts]);
  return { options, isPending };
}

/**
 * BR-FIN-03-05 — an income receipt credits revenue, liability or equity;
 * never an asset or an expense. Postable and active, like any posting target.
 */
export function useCreditAccountOptions(): { options: AccountOption[]; isPending: boolean } {
  const { data: accounts, isPending } = useAccounts();
  const options = useMemo(
    () =>
      (accounts ?? [])
        .filter(
          (account) =>
            account.postable &&
            account.active &&
            account.type !== 'asset' &&
            account.type !== 'expense',
        )
        .map(toOption),
    [accounts],
  );
  return { options, isPending };
}

/**
 * BR-FIN-04-05 (and the bill lines' rule) — "only expense accounts are
 * offered": the postable, active leaves of the expense tree. A payment can
 * never debit a revenue account; a bill line can only debit an expense one.
 */
export function useExpenseAccountOptions(): { options: AccountOption[]; isPending: boolean } {
  const { data: accounts, isPending } = useAccounts();
  const options = useMemo(
    () =>
      (accounts ?? [])
        .filter((account) => account.postable && account.active && account.type === 'expense')
        .map(toOption),
    [accounts],
  );
  return { options, isPending };
}
